/**
 * PulsePill — Pulse entry in the gamification strip.
 *
 * Renders as active (tappable) after first Pulse completion, greyed/disabled
 * for users who have never taken a Pulse, and not rendered at all when the
 * user's organization does not have Pulse enabled.
 *
 * Layout: [pulse_head icon (outside)] [pill with score]
 * Score defaults to 0 when the user has no score yet (inactive or first-time).
 *
 * Styling matches the existing coins pill pattern in SubHeader:
 *   - 25px pill height, border-radius: 20px, semi-transparent background
 *   - Icon outside the pill to the left, sized to match pill height
 *
 * BHPD-5151
 */
import React from "react";
import styled from "styled-components";
import { color, fontFamily, fontSize, spacingDefaults } from "style/constants";
import { ReactComponent as PulseBrainSvg } from "assets/pulse_pill_brain.svg";
import { ReactComponent as PulseBrainDisabledSvg } from "assets/pulse_pill_brain_disabled.svg";

export interface PulsePillProps {
  /** Whether the user has completed at least one Pulse */
  isActive: boolean;
  /** Whether the user's organization has Pulse enabled */
  isPulseEnabled: boolean;
  /** Callback when the active pill is tapped */
  onPress: () => void;
  /** The user's latest Pulse index score. Defaults to 0. */
  score?: number;
}

interface PillButtonProps {
  isActive: boolean;
}

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacingDefaults.xsmall};
  height: 25px;

  svg {
    height: 100%;
    width: auto;
  }
`;

const PillButton = styled(
  ({
    isActive,
    ...props
  }: PillButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button {...props} />
  )
)<PillButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 25px;
  padding: 0 ${spacingDefaults.normal};
  border-radius: 20px;
  border: none;
  font-family: ${fontFamily.primary};
  font-size: ${fontSize.small};
  font-weight: 600;
  background-color: ${props =>
    props.isActive ? color.SCORE_CHART_TOOLTIP : color.GRAY_PROGESS_BAR};
  color: ${color.BLACK};
  cursor: ${props => (props.isActive ? "pointer" : "default")};
`;

const PulsePill: React.FC<PulsePillProps> = ({
  isActive,
  isPulseEnabled,
  onPress,
  score = 0
}) => {
  if (!isPulseEnabled) {
    return null;
  }

  return (
    <Wrapper>
      {isActive ? (
        <PulseBrainSvg aria-hidden="true" />
      ) : (
        <PulseBrainDisabledSvg aria-hidden="true" />
      )}
      <PillButton
        isActive={isActive}
        disabled={!isActive}
        aria-label="Pulse"
        onClick={isActive ? onPress : undefined}
      >
        {String(Math.round(score)).padStart(3, "0")}
      </PillButton>
    </Wrapper>
  );
};

export default PulsePill;
